import * as XLSX from 'xlsx';

import { ILayer } from '../types/ILayer';
import { ELayerType } from '../types/ELayerType';
import { LayerUtils } from '../util/LayerUtils';
import { Filename } from './Filename';

/**
 * Exports layer data to Excel format.
 *
 * For CurrentSpeed layers, exports columns: Longitude, Latitude, U, V
 * For other layers, exports columns: Longitude, Latitude, Value
 */
class LayerExporterExcel {

  /**
   * Creates column headers based on layer type.
   * Uses the layer's name and unit from its definition.
   */
  private static getHeaders(layer: ILayer): string[] {
    const def = LayerUtils.layerTypeToDefinition(layer.type);
    const unit = def.unit;

    if (layer.type === ELayerType.CurrentSpeed) {
      // CurrentSpeed has U and V components
      const unitStr = unit ? ` (${unit})` : '';
      return [
        'Longitude (degree)',
        'Latitude (degree)',
        `U${unitStr}`,
        `V${unitStr}`
      ];
    } else {
      // Single value layers - use the layer's name (e.g., "Depth", "Daily POC")
      const unitStr = unit ? ` (${unit})` : '';
      return [
        'Longitude (degree)',
        'Latitude (degree)',
        `${def.name}${unitStr}`
      ];
    }
  }

  /**
   * Creates data rows from layer data points.
   */
  private static getRows(layer: ILayer): (number | null)[][] {
    if (layer.type === ELayerType.CurrentSpeed) {
      return layer.data.map(p => [
        p.lng,
        p.lat,
        p.value[0], // U component
        p.value[1]  // V component
      ]);
    } else {
      return layer.data.map(p => [
        p.lng,
        p.lat,
        p.value[0]
      ]);
    }
  }

  /**
   * Exports a layer to an Excel file and triggers download.
   */
  static export(layer: ILayer): void {
    const headers = this.getHeaders(layer);
    const rows = this.getRows(layer);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Set column widths
    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    const filename = Filename.sanitize(layer.source || 'layer-export');
    XLSX.writeFile(workbook, `${filename}.xlsx`, { compression: true });
  }
}

export { LayerExporterExcel }
